import type { CustomFieldValues } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';

/**
 * Fondation de donnees de l'app Cat Manager Six (bar a chats).
 *
 * Regles de la cage :
 *  - acces donnees UNIQUEMENT via `@taki/sdk` (aucun import firebase/firestore) ;
 *  - les chats sont des `asset` de type 'cat', les soins des `careRecord`, les
 *    visites des `booking` de type 'visit' ; le vocabulaire metier vit dans le
 *    champ `type` et le sac `customFields`, jamais dans une entite inventee ;
 *  - ce fichier est le CONTRAT partage par les ecrans (memes noms d'exports et
 *    memes cles customFields).
 */

// --- Capacite du bar / horaires -------------------------------------------

export const SEAT_TABLES = 6; // 6 tables (groupes)
export const SEAT_STOOLS = 5; // 5 postes table haute (1 personne)
export const OPEN_HOUR_START = 10; // 10h
export const OPEN_HOUR_END = 18; // 18h
export const OPEN_DAYS = [1, 2, 3, 4, 5] as const; // lundi(1) a vendredi(5), getDay()

// --- Types metier (unions figees) -----------------------------------------

export type AdoptionStatus = 'resident' | 'adoptable' | 'adopte';
export type SeatKind = 'table' | 'stool';
export type CareType = 'vaccination' | 'treatment' | 'note';
export type VisitStatus = 'confirmed' | 'arrived' | 'cancelled';

/** Bascule d'onglet racine partagee par tous les ecrans. */
export type AppTab = 'cats' | 'visits';

// --- Caracteres (value ascii stockee, label FR affiche) -------------------

export interface CharacterOption {
  value: string;
  label: string;
}

export const CHARACTER_OPTIONS: CharacterOption[] = [
  { value: 'calin', label: 'Calin' },
  { value: 'joueur', label: 'Joueur' },
  { value: 'timide', label: 'Timide' },
  { value: 'curieux', label: 'Curieux' },
  { value: 'independant', label: 'Independant' },
  { value: 'affectueux', label: 'Affectueux' },
  { value: 'gourmand', label: 'Gourmand' },
  { value: 'dormeur', label: 'Dormeur' },
];

// --- Statuts d'adoption (value + libelle FR + ton StatusBadge) -------------

export interface AdoptionStatusOption {
  value: AdoptionStatus;
  label: string;
  tone: 'success' | 'neutral' | 'accent';
}

export const ADOPTION_STATUSES: AdoptionStatusOption[] = [
  { value: 'resident', label: 'Resident', tone: 'neutral' },
  { value: 'adoptable', label: 'Adoptable', tone: 'accent' },
  { value: 'adopte', label: 'Adopte', tone: 'success' },
];

// --- Types de soin (value + libelle FR) pour le formulaire d'ajout ---------

export interface CareTypeOption {
  value: CareType;
  label: string;
}

export const CARE_TYPES: CareTypeOption[] = [
  { value: 'vaccination', label: 'Vaccination' },
  { value: 'treatment', label: 'Traitement' },
  { value: 'note', label: 'Note' },
];

// --- Statuts de visite (champ natif `status` du booking) -------------------

export interface VisitStatusOption {
  value: VisitStatus;
  label: string;
  tone: 'success' | 'neutral' | 'accent';
}

export const VISIT_STATUSES: VisitStatusOption[] = [
  { value: 'confirmed', label: 'Prevue', tone: 'accent' },
  { value: 'arrived', label: 'Arrivee', tone: 'success' },
  { value: 'cancelled', label: 'Annulee', tone: 'neutral' },
];

// --- Seed (15 noms EXACTS, ordre impose) -----------------------------------

export const SEED_CATS: string[] = [
  'Peter', 'Marine', 'Minou', 'Joumo', 'Koukou', 'Paul', 'Mimi', 'Jospine',
  'Rudo', 'Lax', 'Paulo', 'Marina', 'Samba', 'Felix', 'Aaix',
];

// --- Types vue (derives des familles socle) --------------------------------

export interface Cat {
  id: string;
  name: string;
  character: string[];
  adoptionStatus: AdoptionStatus;
  photoUrl: string;
  birthYear: number | null;
  diet: string;
  vaccinesUpToDate: boolean;
}

export interface Care {
  id: string;
  type: CareType;
  date: string; // yyyy-mm-dd, derive du champ natif performedAt
  description: string;
}

export interface Visit {
  id: string;
  startAt: Date | null; // reconstruit depuis le champ natif
  endAt: Date | null;
  partySize: number;
  seatKind: SeatKind;
  clientName: string;
  status: string;
}

// --- Helpers internes de lecture defensive ---------------------------------

/** Recupere le sac customFields d'un document quel qu'il soit, sans planter. */
function customFieldsOf(source: unknown): Record<string, unknown> {
  if (source && typeof source === 'object' && 'customFields' in source) {
    const cf = (source as { customFields?: unknown }).customFields;
    if (cf && typeof cf === 'object') {
      return cf as Record<string, unknown>;
    }
  }
  return {};
}

/** Lit une propriete de premier niveau d'un objet inconnu. */
function fieldOf(source: unknown, key: string): unknown {
  if (source && typeof source === 'object' && key in source) {
    return (source as Record<string, unknown>)[key];
  }
  return undefined;
}

/** Convertit une valeur Timestamp | Date | string | inconnu en Date ou null. */
function toDate(value: unknown): Date | null {
  if (value == null) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'object' && typeof (value as { toDate?: unknown }).toDate === 'function') {
    try {
      const d = (value as { toDate: () => Date }).toDate();
      return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
    } catch {
      return null;
    }
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

// --- Helpers de mapping publics --------------------------------------------

/** Fallback fige (statut par defaut) : evite tout acces indexe non verifie. */
const DEFAULT_ADOPTION_STATUS: AdoptionStatusOption = {
  value: 'resident',
  label: 'Resident',
  tone: 'neutral',
};

/** Caste une valeur libre vers l'option de statut d'adoption (defaut 'resident'). */
export function adoptionStatusOf(value: string): AdoptionStatusOption {
  return ADOPTION_STATUSES.find((s) => s.value === value) ?? DEFAULT_ADOPTION_STATUS;
}

/** Date du jour au format yyyy-mm-dd : valeur par defaut des formulaires. */
export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Fallback fige du statut de visite (defaut 'confirmed'). */
const DEFAULT_VISIT_STATUS: VisitStatusOption = {
  value: 'confirmed',
  label: 'Prevue',
  tone: 'accent',
};

/** Caste une valeur libre vers l'option de statut de visite (defaut 'confirmed'). */
export function visitStatusOf(value: string): VisitStatusOption {
  return VISIT_STATUSES.find((s) => s.value === value) ?? DEFAULT_VISIT_STATUS;
}

/**
 * Serialise une vue Cat vers le sac `customFields` complet.
 *
 * Depuis le correctif SDK, update() fusionne customFields cle par cle : il
 * suffirait de n'envoyer que les cles modifiees. Renvoyer toutes les cles de
 * l'app reste correct (fusion : memes valeurs reposees) et garde ce module
 * simple ; les cles posees par d'autres apps ne sont jamais touchees.
 */
export function catToCustomFields(cat: Cat): CustomFieldValues {
  return {
    character: cat.character,
    adoptionStatus: cat.adoptionStatus,
    photoUrl: cat.photoUrl,
    birthYear: cat.birthYear,
    diet: cat.diet,
    vaccinesUpToDate: cat.vaccinesUpToDate,
  };
}

/** Lit un asset (type 'cat') vers la vue Cat. */
export function assetToCat(asset: unknown): Cat {
  const cf = customFieldsOf(asset);
  const idRaw = fieldOf(asset, 'id');
  const nameRaw = fieldOf(asset, 'name');
  const statusRaw = typeof cf.adoptionStatus === 'string' ? cf.adoptionStatus : 'resident';
  return {
    id: typeof idRaw === 'string' ? idRaw : '',
    name: typeof nameRaw === 'string' ? nameRaw : '',
    character: Array.isArray(cf.character) ? (cf.character as string[]) : [],
    adoptionStatus: adoptionStatusOf(statusRaw).value,
    photoUrl: typeof cf.photoUrl === 'string' ? cf.photoUrl : '',
    birthYear: typeof cf.birthYear === 'number' ? cf.birthYear : null,
    diet: typeof cf.diet === 'string' ? cf.diet : '',
    vaccinesUpToDate: cf.vaccinesUpToDate === true,
  };
}

/** Lit un careRecord vers la vue Care. */
export function careRecordToCare(record: unknown): Care {
  const cf = customFieldsOf(record);
  const idRaw = fieldOf(record, 'id');
  const typeRaw = fieldOf(record, 'type');
  const type: CareType =
    typeRaw === 'vaccination' || typeRaw === 'treatment' || typeRaw === 'note' ? typeRaw : 'note';
  const performed = toDate(fieldOf(record, 'performedAt'));
  return {
    id: typeof idRaw === 'string' ? idRaw : '',
    type,
    date: performed != null ? performed.toISOString().slice(0, 10) : '',
    description: typeof cf.description === 'string' ? cf.description : '',
  };
}

/** Lit un booking (type 'visit') vers la vue Visit. */
export function bookingToVisit(booking: unknown): Visit {
  const cf = customFieldsOf(booking);
  const idRaw = fieldOf(booking, 'id');
  const statusRaw = fieldOf(booking, 'status');
  return {
    id: typeof idRaw === 'string' ? idRaw : '',
    startAt: toDate(fieldOf(booking, 'startAt')),
    endAt: toDate(fieldOf(booking, 'endAt')),
    partySize: typeof cf.partySize === 'number' ? cf.partySize : 1,
    seatKind: cf.seatKind === 'stool' ? 'stool' : 'table',
    clientName: typeof cf.clientName === 'string' ? cf.clientName : '',
    status: typeof statusRaw === 'string' ? statusRaw : '',
  };
}

// --- Seed des chats --------------------------------------------------------

/**
 * Cree un asset 'cat' par nom de SEED_CATS, dans l'ordre, en sequentiel.
 * Le SDK remplit les champs standard (id, dates, auteurs...) et cadre par orgId.
 */
export async function seedCats(sdk: TakiSdk, orgId: string): Promise<void> {
  for (const name of SEED_CATS) {
    await sdk.assets.create({
      orgId,
      type: 'cat',
      name,
      customFields: {
        character: [],
        adoptionStatus: 'resident',
        photoUrl: '',
        birthYear: null,
        diet: '',
        vaccinesUpToDate: true,
      },
    });
  }
}
